import React, { useEffect, useState } from 'react';
import axiosInstance from '../lib/axiosInstance';

export default function BankDetails() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [bankList, setBankList] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingBankId, setEditingBankId] = useState(null);
    const [form, setForm] = useState({
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        branchName: '',
        panNumber: '',
        address: ''
    });
    const [formErrors, setFormErrors] = useState({});

    const loadBankDetails = async () => {
        try {
            setLoading(true);
            setError('');
            const { data } = await axiosInstance.get('/brand/bank-details');
            if (data.success) {
                setBankList(data.data || []);
            } else {
                setError(data.message || 'Failed to load bank details');
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load bank details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBankDetails();
    }, []);

    const validateForm = () => {
        const errors = {};
        if (!form.accountHolderName.trim()) errors.accountHolderName = 'Account holder name is required';
        if (!form.accountNumber.trim()) errors.accountNumber = 'Account number is required';
        if (!form.ifscCode.trim()) errors.ifscCode = 'IFSC code is required';
        if (!form.bankName.trim()) errors.bankName = 'Bank name is required';
        if (!form.panNumber.trim()) errors.panNumber = 'PAN number is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setFormErrors((prev) => ({ ...prev, [name]: '' }));
        setError('');
        setError('');
        setSuccess('');
    };

    const handleEdit = (bank) => {
        setForm({
            accountHolderName: bank.accountHolderName || '',
            accountNumber: bank.accountNumber || '',
            ifscCode: bank.ifscCode || '',
            bankName: bank.bankName || '',
            branchName: bank.branchName || '',
            panNumber: bank.panNumber || '',
            address: bank.address || ''
        });
        setEditingBankId(bank.bankDetailID);
        setShowForm(true);
        setFormErrors({});
        setError('');
        setSuccess('');
        // Scroll to the top where the form opens
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!validateForm()) {
            setError('Please fix the errors in the form');
            return;
        }

        try {
            setSaving(true);
            setError('');
            const payload = {
                accountHolderName: form.accountHolderName,
                accountNumber: form.accountNumber,
                ifscCode: form.ifscCode,
                bankName: form.bankName,
                branchName: form.branchName || null,
                panNumber: form.panNumber || null,
                address: form.address || null
            };

            let data;
            if (editingBankId) {
                const response = await axiosInstance.put(`/brand/bank-details/${editingBankId}`, payload);
                data = response.data;
            } else {
                const response = await axiosInstance.post('/brand/bank-details', payload);
                data = response.data;
            }

            if (data.success) {
                setSuccess(editingBankId 
                    ? 'Bank details updated successfully! They will be reviewed by admin.' 
                    : 'Bank details submitted successfully! They will be reviewed by admin.');
                setForm({
                    accountHolderName: '',
                    accountNumber: '',
                    ifscCode: '',
                    bankName: '',
                    branchName: '',
                    panNumber: '',
                    address: ''
                });
                setEditingBankId(null);
                setShowForm(false);
                await loadBankDetails();
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(data.message || 'Failed to submit bank details');
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to submit bank details');
        } finally {
            setSaving(false);
        }
    };

    const getStatusBadgeStyles = (status) => {
        switch (status) {
            case 'active':
                return { background: '#dcfce7', color: '#166534', borderColor: '#22c55e' };
            case 'pending':
                return { background: '#fef9c3', color: '#854d0e', borderColor: '#eab308' };
            case 'rejected':
                return { background: '#fee2e2', color: '#991b1b', borderColor: '#f97373' };
            default:
                return { background: '#e5e7eb', color: '#374151', borderColor: '#d1d5db' };
        }
    };

    return (
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h1 className="lux-heading" style={{ fontSize: 24, fontWeight: 700 }}>Bank Details</h1>
                <button
                    type="button"
                    onClick={() => {
                        if (showForm) {
                            setShowForm(false);
                            setEditingBankId(null);
                            setForm({
                                accountHolderName: '',
                                accountNumber: '',
                                ifscCode: '',
                                bankName: '',
                                branchName: '',
                                panNumber: '',
                                address: ''
                            });
                        } else {
                            setShowForm(true);
                        }
                    }}
                    style={{
                        padding: '8px 16px',
                        borderRadius: 6,
                        border: '1px solid var(--border)',
                        background: showForm ? 'transparent' : 'var(--primary)',
                        color: showForm ? 'var(--text)' : 'white',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600
                    }}
                >
                    {showForm ? 'Cancel' : 'Add Bank Details'}
                </button>
            </div>

            {error && (
                <div className="card" style={{ marginBottom: 16, padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 8, fontSize: 14 }}>
                    {error}
                </div>
            )}

            {success && (
                <div className="card" style={{ marginBottom: 16, padding: 12, background: '#d1fae5', border: '1px solid #6ee7b7', color: '#065f46', borderRadius: 8, fontSize: 14 }}>
                    {success}
                </div>
            )}

            {showForm && (
                <form onSubmit={handleSubmit} className="card" style={{ marginBottom: 24, padding: 20, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                        <div>
                            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Account Holder Name *</label>
                            <input
                                type="text"
                                name="accountHolderName"
                                value={form.accountHolderName}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: 6,
                                    border: `1px solid ${formErrors.accountHolderName ? '#f97373' : 'var(--border)'}`,
                                    background: 'var(--bg)',
                                    color: 'var(--text)',
                                    fontSize: 14
                                }}
                                placeholder="Enter account holder name"
                            />
                            {formErrors.accountHolderName && (
                                <div style={{ marginTop: 4, fontSize: 12, color: '#b91c1c' }}>{formErrors.accountHolderName}</div>
                            )}
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Account Number *</label>
                            <input
                                type="text"
                                name="accountNumber"
                                value={form.accountNumber}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: 6,
                                    border: `1px solid ${formErrors.accountNumber ? '#f97373' : 'var(--border)'}`,
                                    background: 'var(--bg)',
                                    color: 'var(--text)',
                                    fontSize: 14
                                }}
                                placeholder="Enter account number"
                            />
                            {formErrors.accountNumber && (
                                <div style={{ marginTop: 4, fontSize: 12, color: '#b91c1c' }}>{formErrors.accountNumber}</div>
                            )}
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>IFSC Code *</label>
                            <input
                                type="text"
                                name="ifscCode"
                                value={form.ifscCode}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: 6,
                                    border: `1px solid ${formErrors.ifscCode ? '#f97373' : 'var(--border)'}`,
                                    background: 'var(--bg)',
                                    color: 'var(--text)',
                                    fontSize: 14,
                                    textTransform: 'uppercase'
                                }}
                                placeholder="Enter IFSC code"
                            />
                            {formErrors.ifscCode && (
                                <div style={{ marginTop: 4, fontSize: 12, color: '#b91c1c' }}>{formErrors.ifscCode}</div>
                            )}
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Bank Name *</label>
                            <input
                                type="text"
                                name="bankName"
                                value={form.bankName}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: 6,
                                    border: `1px solid ${formErrors.bankName ? '#f97373' : 'var(--border)'}`,
                                    background: 'var(--bg)',
                                    color: 'var(--text)',
                                    fontSize: 14
                                }}
                                placeholder="Enter bank name"
                            />
                            {formErrors.bankName && (
                                <div style={{ marginTop: 4, fontSize: 12, color: '#b91c1c' }}>{formErrors.bankName}</div>
                            )}
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Branch Name (Optional)</label>
                            <input
                                type="text"
                                name="branchName"
                                value={form.branchName}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: 6,
                                    border: '1px solid var(--border)',
                                    background: 'var(--bg)',
                                    color: 'var(--text)',
                                    fontSize: 14
                                }}
                                placeholder="Enter branch name"
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>PAN Number *</label>
                            <input
                                type="text"
                                name="panNumber"
                                value={form.panNumber}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: 6,
                                    border: `1px solid ${formErrors.panNumber ? '#f97373' : 'var(--border)'}`,
                                    background: 'var(--bg)',
                                    color: 'var(--text)',
                                    fontSize: 14,
                                    textTransform: 'uppercase'
                                }}
                                placeholder="Enter PAN number"
                            />
                            {formErrors.panNumber && (
                                <div style={{ marginTop: 4, fontSize: 12, color: '#b91c1c' }}>{formErrors.panNumber}</div>
                            )}
                        </div>

                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Address (Optional)</label>
                            <textarea
                                name="address"
                                value={form.address}
                                onChange={handleChange}
                                rows={3}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: 6,
                                    border: '1px solid var(--border)',
                                    background: 'var(--bg)',
                                    color: 'var(--text)',
                                    fontSize: 14,
                                    resize: 'vertical'
                                }}
                                placeholder="Enter bank address"
                            />
                        </div>
                    </div>

                    <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <button
                            type="button"
                            onClick={() => {
                                setShowForm(false);
                                setEditingBankId(null);
                                setForm({
                                    accountHolderName: '',
                                    accountNumber: '',
                                    ifscCode: '',
                                    bankName: '',
                                    branchName: '',
                                    panNumber: '',
                                    address: ''
                                });
                                setFormErrors({});
                            }}
                            disabled={saving}
                            style={{
                                padding: '8px 16px',
                                borderRadius: 6,
                                border: '1px solid var(--border)',
                                background: 'transparent',
                                color: 'var(--text)',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                fontSize: 14,
                                fontWeight: 500
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            style={{
                                padding: '8px 16px',
                                borderRadius: 6,
                                border: 'none',
                                background: saving ? '#9ca3af' : 'var(--primary)',
                                color: 'white',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                fontSize: 14,
                                fontWeight: 600
                            }}
                        >
                            {saving ? 'Submitting...' : 'Submit for Review'}
                        </button>
                    </div>
                </form>
            )}

            <div className="card" style={{ padding: 20, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h2 style={{ fontSize: 18, fontWeight: 600 }}>Your Bank Details</h2>
                    <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                        You can view all submissions and their current status.
                    </span>
                </div>

                {loading ? (
                    <div style={{ padding: 24, textAlign: 'center', fontSize: 14, color: 'var(--muted)' }}>
                        Loading bank details...
                    </div>
                ) : bankList.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', fontSize: 14, color: 'var(--muted)' }}>
                        No bank details found. Add your bank details to start receiving payouts.
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {bankList.map((bank) => {
                            const statusStyles = getStatusBadgeStyles(bank.status);
                            return (
                                <div
                                    key={bank.bankDetailID}
                                    style={{
                                        borderRadius: 10,
                                        border: '1px solid var(--border)',
                                        padding: 16,
                                        background: 'var(--bg)'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                                        <div>
                                            <div style={{ fontSize: 15, fontWeight: 600 }}>{bank.bankName}</div>
                                            {bank.branchName && (
                                                <div style={{ fontSize: 13, color: 'var(--muted)' }}>{bank.branchName}</div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                                            <span
                                                style={{
                                                    padding: '4px 10px',
                                                    borderRadius: 999,
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    border: `1px solid ${statusStyles.borderColor}`,
                                                    background: statusStyles.background,
                                                    color: statusStyles.color,
                                                    textTransform: 'capitalize'
                                                }}
                                            >
                                                {bank.status}
                                            </span>
                                            <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                                                Submitted on{' '}
                                                {bank.createdAt ? new Date(bank.createdAt).toLocaleString('en-IN', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                }) : '-'}
                                            </span>
                                            {bank.rejectionReason && (
                                                <span style={{ fontSize: 11, color: '#b91c1c' }}>
                                                    Rejection reason: {bank.rejectionReason}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, fontSize: 13, paddingBottom: 16, borderBottom: '1px solid var(--border)', marginBottom: 12 }}>
                                        <div>
                                            <div style={{ color: 'var(--muted)' }}>Account Holder</div>
                                            <div style={{ fontWeight: 500 }}>{bank.accountHolderName}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: 'var(--muted)' }}>Account Number</div>
                                            <div style={{ fontFamily: 'monospace', fontWeight: 500 }}>{bank.accountNumber}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: 'var(--muted)' }}>IFSC Code</div>
                                            <div style={{ fontFamily: 'monospace', fontWeight: 500 }}>{bank.ifscCode}</div>
                                        </div>
                                        {bank.panNumber && (
                                            <div>
                                                <div style={{ color: 'var(--muted)' }}>PAN Number</div>
                                                <div style={{ fontWeight: 500 }}>{bank.panNumber}</div>
                                            </div>
                                        )}
                                        {bank.address && (
                                            <div style={{ gridColumn: '1 / -1' }}>
                                                <div style={{ color: 'var(--muted)' }}>Address</div>
                                                <div style={{ fontWeight: 500 }}>{bank.address}</div>
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <button
                                            type="button"
                                            onClick={() => handleEdit(bank)}
                                            style={{
                                                padding: '6px 14px',
                                                borderRadius: 6,
                                                border: '1px solid var(--border)',
                                                background: 'transparent',
                                                color: 'var(--text)',
                                                cursor: 'pointer',
                                                fontSize: 13,
                                                fontWeight: 500,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6
                                            }}
                                            title="Edit this bank detail - Note: Any edits will require admin re-approval"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                            Edit
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}


